import { Exploration, ExplorationMetadata, Model } from '@/explore/types';
import { getCellKindCounts } from '@/core/cell';

import { getModelDetailExploration, modelSupportsDetailExploration } from '../detail-exploration';
import { buildModelExploration } from '.';

/**
 * Locally-built explorations (models, detail views) carry their `view`, so their
 * stats are computed from the cells; server-loaded explorations already come with
 * stats from the metadata query.
 */
const withStats = <T extends Exploration | ExplorationMetadata>(exploration: T): T =>
  'view' in exploration
    ? { ...exploration, stats: { cellKindCounts: getCellKindCounts(exploration.view.cells) } }
    : exploration;

interface BuildExplorationsInput<T extends Exploration | ExplorationMetadata> {
  models: Model[];
  savedExplorations: T[];
  savedDetailExplorations: T[];
}

/**
 * Assembles the full list of explorations available in an account by combining the
 * server-loaded explorations (user-saved + parameterised detail explorations) with
 * the model and model-detail explorations synthesized client-side from models.
 *
 * A synthesized model-detail exploration is excluded when a saved parameterised
 * detail exploration already exists for the same id, so a user's customized and
 * persisted detail exploration wins. Every exploration is stamped with its own id
 * as `explorationSourceId`.
 */
export function buildExplorations(input: BuildExplorationsInput<Exploration>): Exploration[];
export function buildExplorations(
  input: BuildExplorationsInput<ExplorationMetadata>,
): ExplorationMetadata[];
export function buildExplorations({
  models,
  savedExplorations,
  savedDetailExplorations,
}: BuildExplorationsInput<Exploration | ExplorationMetadata>): Array<
  Exploration | ExplorationMetadata
> {
  const modelDetailExplorations = models
    .filter(modelSupportsDetailExploration)
    .map(getModelDetailExploration)
    .filter(({ explorationId }) =>
      savedDetailExplorations.every((e) => e.explorationId !== explorationId),
    );

  return [
    ...savedExplorations,
    ...savedDetailExplorations,
    ...modelDetailExplorations,
    ...models.map((model) => buildModelExploration(model)),
  ].map((exploration) => ({
    ...withStats(exploration),
    options: { ...exploration.options, explorationSourceId: exploration.explorationId },
  }));
}
